import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  InputNumber,
  message,
  Space,
  Row,
  Col,
  Card,
  Divider,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  ExpenseCategory,
  ExpenseTransaction,
  CreateExpenseTransactionRequest,
  UpdateExpenseTransactionRequest,
  getExpenseCategories,
  createExpenseTransaction,
  updateExpenseTransaction,
  createExpenseCategory,
} from '../../services/expenseService';

interface ExpenseTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction?: ExpenseTransaction;
  onSuccess?: () => void;
}

const CATEGORY_ICONS = [
  '🍽️', '🚗', '🛒', '📄', '🎮', '🏥', '📚', '💡', '🏠', '👕', '🎬', '✈️', '💊', '🎨', '🏋️', '📱'
];

const CATEGORY_COLORS = [
  '#EF4444', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#6B7280',
  '#84CC16', '#F97316', '#6366F1', '#14B8A6', '#F43F5E', '#A855F7', '#EAB308', '#22C55E'
];

export const ExpenseTransactionModal: React.FC<ExpenseTransactionModalProps> = ({
  isOpen,
  onClose,
  transaction,
  onSuccess,
}) => {
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryForm] = Form.useForm();

  const isEdit = !!transaction;

  useEffect(() => {
    if (isOpen) {
      loadCategories();
      if (transaction) {
        form.setFieldsValue({
          category_id: transaction.category_id,
          amount: transaction.amount,
          expense_date: dayjs(transaction.expense_date),
          notes: transaction.notes || '',
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          expense_date: dayjs(),
        });
      }
    }
  }, [isOpen, transaction, form]);

  const loadCategories = async () => {
    try {
      const categoriesData = await getExpenseCategories();
      setCategories(categoriesData);
    } catch (error) {
      message.error('Không thể tải danh mục chi tiêu');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const submitData = {
        category_id: values.category_id,
        amount: values.amount,
        expense_date: values.expense_date.format('YYYY-MM-DD'),
        notes: values.notes || '',
      };

      if (isEdit && transaction) {
        await updateExpenseTransaction(transaction.id, submitData);
        message.success('Cập nhật giao dịch thành công');
      } else {
        await createExpenseTransaction(submitData);
        message.success('Thêm giao dịch thành công');
      }
      
      onSuccess?.();
      onClose();
    } catch (error: any) {
      if (error.errorFields) {
        message.error('Vui lòng nhập đầy đủ thông tin');
      } else {
        message.error('Có lỗi xảy ra khi lưu giao dịch');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async () => {
    try {
      const values = await categoryForm.validateFields();
      setLoading(true);

      await createExpenseCategory({
        name: values.name,
        icon: values.icon,
        color: values.color,
      });

      setShowCategoryModal(false);
      categoryForm.resetFields();
      await loadCategories();
      message.success('Thêm danh mục thành công');
    } catch (error: any) {
      if (error.errorFields) {
        message.error('Vui lòng nhập tên danh mục');
      } else {
        message.error('Có lỗi xảy ra khi thêm danh mục');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        title={isEdit ? 'Chỉnh sửa giao dịch' : 'Thêm giao dịch mới'}
        open={isOpen}
        onCancel={onClose}
        footer={[
          <Button key="cancel" onClick={onClose}>
            Hủy
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={handleSubmit}
          >
            {isEdit ? 'Cập nhật' : 'Thêm'}
          </Button>,
        ]}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            expense_date: dayjs(),
          }}
        >
          <Form.Item
            name="amount"
            label="Số tiền"
            rules={[{ required: true, message: 'Vui lòng nhập số tiền' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="Nhập số tiền"
              min={0}
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            />
          </Form.Item>

          <Form.Item
            name="category_id"
            label="Danh mục"
            rules={[{ required: true, message: 'Vui lòng chọn danh mục' }]}
          >
            <Select
              placeholder="Chọn danh mục"
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: '8px 0' }} />
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    onClick={() => setShowCategoryModal(true)}
                    style={{ width: '100%' }}
                  >
                    Thêm danh mục mới
                  </Button>
                </>
              )}
            >
              {categories.map((category) => (
                <Select.Option key={category.id} value={category.id}>
                  <Space>
                    <span>{category.icon}</span>
                    <span>{category.name}</span>
                  </Space>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="expense_date"
            label="Ngày chi tiêu"
            rules={[{ required: true, message: 'Vui lòng chọn ngày' }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              placeholder="Chọn ngày"
            />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Ghi chú"
          >
            <Input.TextArea
              placeholder="Ghi chú (tùy chọn)"
              rows={3}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal thêm danh mục mới */}
      <Modal
        title="Thêm danh mục mới"
        open={showCategoryModal}
        onCancel={() => setShowCategoryModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowCategoryModal(false)}>
            Hủy
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={handleAddCategory}
          >
            Thêm
          </Button>,
        ]}
        width={600}
      >
        <Form
          form={categoryForm}
          layout="vertical"
          initialValues={{
            icon: '💰',
            color: '#3B82F6',
          }}
        >
          <Form.Item
            name="name"
            label="Tên danh mục"
            rules={[{ required: true, message: 'Vui lòng nhập tên danh mục' }]}
          >
            <Input placeholder="Nhập tên danh mục" />
          </Form.Item>

          <Form.Item
            name="icon"
            label="Icon"
          >
            <Select placeholder="Chọn icon">
              {CATEGORY_ICONS.map((icon) => (
                <Select.Option key={icon} value={icon}>
                  {icon}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="color"
            label="Màu sắc"
          >
            <Select placeholder="Chọn màu sắc">
              {CATEGORY_COLORS.map((color) => (
                <Select.Option key={color} value={color}>
                  <Space>
                    <div
                      style={{
                        width: 20,
                        height: 20,
                        backgroundColor: color,
                        borderRadius: '50%',
                        display: 'inline-block',
                      }}
                    />
                    <span>{color}</span>
                  </Space>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};